import type { ProjectSummary } from '@shared/api';
import { COMPARED_FIELDS, type DuplicateAccountSummary, type DuplicateCandidateDto } from '@shared/duplicates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCheck, GitMerge, Split } from 'lucide-react';
import { useSearchParams } from 'react-router';
import { Button } from '../../components/ui/button';
import { Card, CardBody, CardHeader } from '../../components/ui/card';
import { Alert, Badge, EmptyState, Spinner } from '../../components/ui/feedback';
import { Select } from '../../components/ui/form';
import { api } from '../../lib/api';
import { cn } from '../../lib/cn';
import { queryKeys } from '../../lib/query';
import { errorMessage } from '../../lib/session';

function valueOf(account: DuplicateAccountSummary, key: keyof DuplicateAccountSummary): string {
  const value = account[key];
  if (value === null || value === undefined || value === '') return '—';
  return Array.isArray(value) ? value.join(', ') : String(value);
}

function Side({
  account,
  other,
  label,
  onMerge,
  busy,
}: {
  account: DuplicateAccountSummary;
  other: DuplicateAccountSummary;
  label: string;
  onMerge: () => void;
  busy: boolean;
}) {
  return (
    <div className="min-w-0 flex-1 rounded-[var(--radius-panel)] border border-border p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <Badge tone="neutral">
          {account.sourceCount} source{account.sourceCount === 1 ? '' : 's'}
        </Badge>
      </div>
      <dl className="space-y-1">
        {COMPARED_FIELDS.map((field) => {
          const mine = valueOf(account, field.key);
          const theirs = valueOf(other, field.key);
          const differs = mine !== theirs && mine !== '—' && theirs !== '—';
          return (
            <div key={String(field.key)} className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-[13px]">
              <dt className="text-muted-foreground">{field.label}</dt>
              <dd className={cn('min-w-0 truncate', differs ? 'font-medium text-warning' : 'text-foreground')} title={mine}>
                {mine}
              </dd>
            </div>
          );
        })}

        {/* A match reason can cite a shared secondary phone or email, so show every value:
            otherwise the card looks like a contradiction of its own evidence. */}
        {(['phones', 'emails'] as const).map((key) =>
          account[key].length > 1 ? (
            <div key={key} className="grid grid-cols-[88px_minmax(0,1fr)] gap-2 text-[13px]">
              <dt className="text-muted-foreground">{key === 'phones' ? 'All phones' : 'All emails'}</dt>
              <dd className="min-w-0 text-foreground">
                {account[key].map((value) => (
                  <span
                    key={value}
                    className={cn('block truncate', other[key].includes(value) && 'font-medium text-accent')}
                    title={other[key].includes(value) ? `${value} — also on the other business` : value}
                  >
                    {value}
                    {other[key].includes(value) ? ' (shared)' : ''}
                  </span>
                ))}
              </dd>
            </div>
          ) : null,
        )}
      </dl>
      <Button className="mt-3 w-full" size="sm" variant="outline" icon={<GitMerge className="h-3.5 w-3.5" />} loading={busy} onClick={onMerge}>
        Keep this one, merge the other in
      </Button>
    </div>
  );
}

/** Review queue for pairs the merge step was not confident enough to join automatically. */
export function DuplicatesPage() {
  const [params, setParams] = useSearchParams();
  const projectId = params.get('project') ?? undefined;
  const client = useQueryClient();

  const projects = useQuery({ queryKey: queryKeys.projects, queryFn: () => api.get<ProjectSummary[]>('/api/projects') });

  const duplicates = useQuery({
    queryKey: queryKeys.duplicates(projectId),
    queryFn: () =>
      api.get<DuplicateCandidateDto[]>(`/api/duplicates?status=pending${projectId ? `&projectId=${encodeURIComponent(projectId)}` : ''}`),
  });

  const resolve = useMutation({
    mutationFn: (input: { id: string; action: 'merge' | 'dismiss'; primaryId?: string }) =>
      api.post<{ ok: true }>(`/api/duplicates/${encodeURIComponent(input.id)}/resolve`, {
        action: input.action,
        ...(input.primaryId ? { primaryId: input.primaryId } : {}),
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ['duplicates'] }),
        client.invalidateQueries({ queryKey: ['accounts'] }),
        client.invalidateQueries({ queryKey: queryKeys.projects }),
      ]);
    },
  });

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-5">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Duplicate review</h1>
          <p className="mt-0.5 text-[13px] text-muted-foreground">
            Pairs that looked similar but not similar enough to merge automatically. A false merge loses data, so these wait for you.
          </p>
        </div>
        <label className="flex items-center gap-1.5">
          <span className="text-[13px] text-muted-foreground">Project</span>
          <Select
            className="h-9 w-48"
            value={projectId ?? ''}
            onChange={(event) => setParams(event.target.value ? { project: event.target.value } : {})}
          >
            <option value="">All projects</option>
            {projects.data?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </label>
      </header>

      {resolve.error ? (
        <Alert className="mb-3" tone="error">
          {errorMessage(resolve.error)}
        </Alert>
      ) : null}

      {duplicates.isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Loading pairs…
        </div>
      ) : (duplicates.data?.length ?? 0) === 0 ? (
        <Card>
          <EmptyState
            icon={<CheckCheck className="h-6 w-6" />}
            title="Nothing to review"
            description="No pairs are waiting. Anything above the auto-merge score was merged during the run."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {duplicates.data?.map((candidate) => (
            <Card key={candidate.id}>
              <CardHeader
                title={
                  <span className="flex flex-wrap items-center gap-2">
                    Possible duplicate
                    <Badge tone="warning">{Math.round(candidate.score * 100)}% match</Badge>
                    {candidate.reasons.map((reason) => (
                      <Badge key={reason.code} tone="neutral">
                        {reason.label}
                      </Badge>
                    ))}
                  </span>
                }
                description="Differing values are highlighted. Merging keeps every source record from both."
              />
              <CardBody>
                <div className="flex flex-col gap-3 md:flex-row">
                  <Side
                    account={candidate.a}
                    other={candidate.b}
                    label="Business A"
                    busy={resolve.isPending}
                    onMerge={() => resolve.mutate({ id: candidate.id, action: 'merge', primaryId: candidate.a.id })}
                  />
                  <Side
                    account={candidate.b}
                    other={candidate.a}
                    label="Business B"
                    busy={resolve.isPending}
                    onMerge={() => resolve.mutate({ id: candidate.id, action: 'merge', primaryId: candidate.b.id })}
                  />
                </div>
                <div className="mt-3 flex justify-end">
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<Split className="h-3.5 w-3.5" />}
                    loading={resolve.isPending}
                    onClick={() => resolve.mutate({ id: candidate.id, action: 'dismiss' })}
                  >
                    These are different businesses — keep separate
                  </Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
